"use client";

import React, { memo, useCallback, useMemo } from "react";
import { Grid, Slider, Text } from "@chakra-ui/react";
import type { GenericOptionItem } from "./option.types";

interface GenericSliderProps<T extends string = string> {
  // Logic
  options: GenericOptionItem<T>[];
  activeState: T[]; // now a generic array of values of type T
  setActiveState: (vals: T[]) => void;
  // UI
  title?: string;
  size?: "sm" | "md" | "lg";
  showMarks?: boolean;
}

function GenericSliderInner<T extends string = string>({
  options,
  activeState,
  setActiveState,
  title,
  size = "md",
  showMarks = true,
}: GenericSliderProps<T>) {
  // Derived bounds from options length
  const derivedMin = 0;
  const derivedMax = Math.max(0, options.length - 1);
  const stepValue = 1;

  // Map external activeState (values of T) to internal slider indices
  const displayIndices = useMemo(
    () =>
      activeState
        .map((val) => options.findIndex((o) => o.value === val))
        .filter((idx) => idx >= 0),
    [activeState, options],
  );

  // Handler: map indices back to option values (T) and call setActiveState
  const handleValueChange = useCallback(
    (vals: { value: number[] } | number[]) => {
      // Accept both shape: { value: number[] } and direct number[] just in case
      const valuesArray: number[] = Array.isArray(vals)
        ? vals
        : (vals.value as number[]);

      const mapped = valuesArray
        .map((idx) => options[idx])
        .filter(Boolean)
        .map((o) => o!.value) as T[];

      setActiveState(mapped);
    },
    [options, setActiveState],
  );

  const markLabels = useMemo(() => {
    if (options.length === 0) return null;
    // Only first, middle, and last marks
    const middleIndex = Math.floor((options.length - 1) / 2);
    return {
      start: options[0].label,
      middle: options[middleIndex].label,
      end: options[options.length - 1].label,
    };
  }, [options]);

  return (
    <Slider.Root
      size={size}
      value={displayIndices}
      min={derivedMin}
      max={derivedMax}
      step={stepValue}
      onValueChange={handleValueChange}
    >
      {title && <Slider.Label>{title}</Slider.Label>}
      <Slider.Control>
        <Slider.Track bg="moveon.inactive">
          <Slider.Range bg="moveon.tertiaryLight" />
        </Slider.Track>
        <Slider.Thumbs />
      </Slider.Control>
      {showMarks && markLabels ? (
        <Grid
          templateColumns="repeat(3, minmax(0, 1fr))"
          columnGap="2"
          mt="3"
          color="moveon.text.muted"
        >
          <Text fontSize="xs" lineHeight="short" textAlign="start">
            {markLabels.start}
          </Text>
          <Text fontSize="xs" lineHeight="short" textAlign="center">
            {markLabels.middle}
          </Text>
          <Text fontSize="xs" lineHeight="short" textAlign="end">
            {markLabels.end}
          </Text>
        </Grid>
      ) : null}
    </Slider.Root>
  );
}

const GenericSlider = memo(GenericSliderInner) as <T extends string = string>(
  props: GenericSliderProps<T>,
) => React.ReactElement;

export default GenericSlider;
